import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import axios from 'axios';
import { noop } from '../helpers';
import { getAvailableDates, StatusService } from '../services';
import { transformDate, transformISODate } from '../utils';
import { useFilters } from '../hooks/useFilters';
import { IItemsFilterProps } from '../components/shared/filters/ItemsFilter';
import { IStatus, ReducerActionMap } from '../types';

export enum GlobalStateTypes {
  SET_FILTERS = 'SET_FILTERS',
  SET_AVAILABLE_DATES = 'SET_AVAILABLE_DATES',
  SET_LAST_APPTIO_RECORD_MONTH = 'SET_LAST_APPTIO_RECORD_MONTH',
  SET_STATUS = 'SET_STATUS',
  SET_SNAPSHOT = 'SET_SNAPSHOT',
  SET_LOADING_FILTERS = 'SET_LOADING_FILTERS',
}

type GlobalStatePayload = {
  [GlobalStateTypes.SET_FILTERS]: { filters: IItemsFilterProps[] };
  [GlobalStateTypes.SET_AVAILABLE_DATES]: { availableDates: string[] };
  [GlobalStateTypes.SET_LAST_APPTIO_RECORD_MONTH]: { lastApptioRecordMonth: string };
  [GlobalStateTypes.SET_STATUS]: { status: IStatus };
  [GlobalStateTypes.SET_SNAPSHOT]: { snapshot: string | undefined };
  [GlobalStateTypes.SET_LOADING_FILTERS]: { status: boolean };
};

type UrlActions = ReducerActionMap<GlobalStatePayload>[keyof ReducerActionMap<GlobalStatePayload>];

export type GlobalStateReducerState = {
  filters: IItemsFilterProps[];
  loadingFilters: boolean;
  availableDates: string[];
  snapshot: string | undefined;
  lastApptioRecordMonth: string | undefined;
  status: IStatus | undefined;
  idType: string | undefined;
};

export const globalStateReducer = (state: GlobalStateReducerState, action: UrlActions): GlobalStateReducerState => {
  switch (action.type) {
    case GlobalStateTypes.SET_FILTERS: {
      const { filters } = action.payload;
      return {
        ...state,
        filters,
      };
    }
    case GlobalStateTypes.SET_AVAILABLE_DATES: {
      const { availableDates } = action.payload;
      return {
        ...state,
        availableDates,
      };
    }
    case GlobalStateTypes.SET_LAST_APPTIO_RECORD_MONTH: {
      const { lastApptioRecordMonth } = action.payload;
      return {
        ...state,
        lastApptioRecordMonth,
      };
    }
    case GlobalStateTypes.SET_STATUS: {
      const { status } = action.payload;
      return {
        ...state,
        status,
      };
    }
    case GlobalStateTypes.SET_SNAPSHOT: {
      const { snapshot } = action.payload;
      return {
        ...state,
        snapshot,
      };
    }
    case GlobalStateTypes.SET_LOADING_FILTERS: {
      const { status } = action.payload;
      return {
        ...state,
        loadingFilters: status,
      };
    }
  }
};

/**
 *  TYPES DECLARATION
 */
type GlobalStateContextType = GlobalStateReducerState & {
  setFilters: (filters: IItemsFilterProps[]) => void;
  setStatus: (status: IStatus) => void;
  setSnapshot: (snapshot: string | undefined) => void;
  setLoadingFilters: (status: boolean) => void;
};

/**
 *  VARIABLES DECLARATION
 */
const initialState: GlobalStateReducerState = {
  filters: [],
  loadingFilters: true,
  availableDates: [],
  snapshot: undefined,
  lastApptioRecordMonth: undefined,
  status: undefined,
  idType: undefined,
};

const GlobalStateContext = createContext<GlobalStateContextType>({
  ...initialState,
  setLoadingFilters: noop,
  setSnapshot: noop,
  setFilters: noop,
  setStatus: noop,
});

GlobalStateContext.displayName = 'UrlContext';

const fetchAvailableDates = async (): Promise<string[]> => {
  const dates = await getAvailableDates();
  const sortedDates = [...dates].sort();
  return sortedDates
    .map((date: string) => transformDate(date))
    .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
};

const fetchLastApptioRecordMonth = async (): Promise<string> => {
  const request = axios.CancelToken.source();
  const status = await StatusService.getStatus(request, true);
  return status.lastApptioRecordMonth;
};

const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(globalStateReducer, initialState);
  const { filters, loading } = useFilters({
    snapshot: state.snapshot,
    availableDates: state.availableDates,
  });

  const setFilters = useCallback(
    (filters: IItemsFilterProps[]) => dispatch({ type: GlobalStateTypes.SET_FILTERS, payload: { filters } }),
    [dispatch]
  );

  const setStatus = useCallback(
    (status: IStatus) => {
      dispatch({ type: GlobalStateTypes.SET_STATUS, payload: { status } });
    },
    [dispatch]
  );

  const setLoadingFilters = useCallback(
    (status: boolean) => {
      dispatch({ type: GlobalStateTypes.SET_LOADING_FILTERS, payload: { status } });
    },
    [dispatch]
  );

  const setSnapshot = useCallback(
    (snapshot: string | undefined) => {
      dispatch({ type: GlobalStateTypes.SET_SNAPSHOT, payload: { snapshot } });
    },
    [dispatch]
  );

  useEffect(() => {
    setFilters(filters);
  }, [filters]);

  useEffect(() => {
    setLoadingFilters(loading);
  }, [loading]);

  /**
   * Initialise context with availableDates and lastApptioRecordMonth
   */
  useEffect(() => {
    // TODO [perfs] check why it is still called too often: `snapshot` starts with `undefined`, then to a month,
    // then `undefined` again, then to a month...
    const getValidSnapshot = (availableDates: string[]): string | null | undefined => {
      return transformISODate(availableDates[availableDates.length - 1]);
    };

    const setAvailableDates = (availableDates: string[]) => {
      dispatch({ type: GlobalStateTypes.SET_AVAILABLE_DATES, payload: { availableDates } });
    };

    const setLastApptioRecordMonth = (lastApptioRecordMonth: string) => {
      dispatch({ type: GlobalStateTypes.SET_LAST_APPTIO_RECORD_MONTH, payload: { lastApptioRecordMonth } });
    };

    fetchLastApptioRecordMonth()
      .then((lastApptioRecordMonth: string) => {
        setLastApptioRecordMonth(lastApptioRecordMonth);
      })
      .then(fetchAvailableDates)
      .then((availableDates) => {
        setAvailableDates(availableDates);
        return getValidSnapshot(availableDates);
      })
      .then((snapshot) => {
        setSnapshot(snapshot || undefined);
      });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setFilters,
      setSnapshot,
      setStatus,
      setLoadingFilters,
    }),
    [state, setFilters, setStatus, setSnapshot, setLoadingFilters]
  );

  return <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>;
};

export const useGlobalState = (): GlobalStateContextType => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error(`useGlobalContext must be used within an UrlProvider`);
  }
  return context;
};

export { GlobalStateContext, GlobalStateProvider };
